import { openDB } from 'idb'

const DB_NAME = 'tourist'
const DB_VERSION = 1

const dbPromise = openDB(DB_NAME, DB_VERSION, {
  upgrade(db) {
    if (!db.objectStoreNames.contains('pois')) db.createObjectStore('pois', { keyPath: 'id' })
    if (!db.objectStoreNames.contains('poi_content')) db.createObjectStore('poi_content', { keyPath: 'key' })
    if (!db.objectStoreNames.contains('bookmarks')) db.createObjectStore('bookmarks', { keyPath: 'id' })
    if (!db.objectStoreNames.contains('settings')) db.createObjectStore('settings')
    if (!db.objectStoreNames.contains('downloads')) db.createObjectStore('downloads', { keyPath: 'city' })
  }
})

export async function putPOIs(items: any[]) {
  const db = await dbPromise
  const tx = db.transaction('pois', 'readwrite')
  for (const it of items) await tx.store.put(it)
  await tx.done
}

export async function getPOIsOffline(lat: number, lon: number, radius_m: number) {
  const db = await dbPromise
  const all = await db.getAll('pois')
  const rdeg = radius_m / 111320
  const minLat = lat - rdeg
  const maxLat = lat + rdeg
  const minLon = lon - rdeg
  const maxLon = lon + rdeg
  const candidates = all.filter(p => p.lat >= minLat && p.lat <= maxLat && p.lon >= minLon && p.lon <= maxLon)
  const R = 6371000
  function hav(a: any) {
    const p1 = lat * Math.PI/180
    const p2 = a.lat * Math.PI/180
    const dphi = (a.lat - lat) * Math.PI/180
    const dl = (a.lon - lon) * Math.PI/180
    const x = Math.sin(dphi/2)**2 + Math.cos(p1)*Math.cos(p2)*Math.sin(dl/2)**2
    return 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1-x)) * R
  }
  const enriched = candidates.map(c => ({ ...c, distance_m: Math.round(hav(c)) }))
  enriched.sort((a,b) => a.distance_m - b.distance_m)
  return enriched
}

export async function savePOIContent(id: string, lang: string, data: any) {
  const db = await dbPromise
  const key = `${id}:${lang}`
  await db.put('poi_content', { key, data })
}

export async function getPOIContent(id: string, lang: string) {
  const db = await dbPromise
  const key = `${id}:${lang}`
  const v = await db.get('poi_content', key)
  if (v) return v.data
  const vEn = await db.get('poi_content', `${id}:en`)
  if (vEn) return vEn.data
  return null
}

export async function getSetting(key: string) {
  const db = await dbPromise
  return await db.get('settings', key)
}

export async function setSetting(key: string, value: any) {
  const db = await dbPromise
  await db.put('settings', value, key)
}

export async function exportBookmarks() {
  const db = await dbPromise
  return await db.getAll('bookmarks')
}

export async function importBookmarks(items: any[]) {
  const db = await dbPromise
  const tx = db.transaction('bookmarks', 'readwrite')
  for (const it of items) await tx.store.put(it)
  await tx.done
}

export async function addBookmark(item: any) {
  const db = await dbPromise
  await db.put('bookmarks', { ...item, pinStatus: item.pinStatus || 'not_pinned' })
}

export async function removeBookmark(id: string) {
  const db = await dbPromise
  await db.delete('bookmarks', id)
}

export async function getBookmarks() {
  const db = await dbPromise
  return await db.getAll('bookmarks')
}

export async function setBookmarkStatus(id: string, status: string) {
  const db = await dbPromise
  const existing = await db.get('bookmarks', id)
  if (!existing) return
  await db.put('bookmarks', { ...existing, pinStatus: status })
}

export async function getBookmark(id: string) {
  const db = await dbPromise
  return await db.get('bookmarks', id)
}

export async function getPOICount() {
  const db = await dbPromise
  return await db.count('pois')
}
